import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { Link } from 'react-router-dom';
import { IconArrowRight, IconBookmark } from '@tabler/icons-react';

export function HomeContent({ isMobile }) {
  const sectionRef = useRef(null);
  const titleRef = useRef(null);
  const subtitleRef = useRef(null);
  const descRef = useRef(null);
  const ctaRef = useRef(null);
  const imageRef = useRef(null);
  const overlayRef = useRef(null);
  const badgeRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Overlay fade
      gsap.fromTo(
        overlayRef.current,
        { opacity: 0 },
        { opacity: 1, duration: 1.2, ease: 'power2.out' }
      );

      // Badge
      gsap.fromTo(
        badgeRef.current,
        { y: -20, opacity: 0, scale: 0.8 },
        { y: 0, opacity: 1, scale: 1, duration: 0.7, ease: 'back.out(1.7)', delay: 0.3 }
      );

      // Title
      gsap.fromTo(
        titleRef.current,
        { y: 60, opacity: 0 },
        { y: 0, opacity: 1, duration: 1, ease: 'power3.out', delay: 0.4 }
      );

      // Subtitle
      gsap.fromTo(
        subtitleRef.current,
        { y: 40, opacity: 0 },
        { y: 0, opacity: 1, duration: 1, ease: 'power3.out', delay: 0.7 }
      );

      // Description
      gsap.fromTo(
        descRef.current,
        { y: 30, opacity: 0 },
        { y: 0, opacity: 1, duration: 1, ease: 'power3.out', delay: 0.9 }
      );

      // CTA buttons
      gsap.fromTo(
        ctaRef.current,
        { y: 30, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.8, ease: 'power2.out', delay: 1.1 }
      );

      // Background image scale
      gsap.fromTo(
        imageRef.current,
        { scale: 1.15 },
        { scale: 1, duration: 2, ease: 'power2.out' }
      );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative w-full min-h-screen flex items-center justify-center overflow-hidden"
    >
      {/* Background Image */}
      <div className="absolute inset-0 z-0">
        <img
          ref={imageRef}
          src={isMobile ? '/assets/building_9_16.jpeg' : '/assets/building.jpeg'}
          alt="Darul Hadees Campus"
          className="w-full h-full object-cover"
          fetchpriority="high"
        />
        {/* Gradient overlays for readability */}
        <div
          ref={overlayRef}
          className="absolute inset-0 opacity-10"
          style={{
            background: 'linear-gradient(135deg, rgba(31, 14, 86, 0.85) 0%, rgba(42, 18, 115, 0.7) 40%, rgba(221, 147, 86, 0.4) 100%)',
          }}
        />
      </div>

      {/* Decorative geometric pattern */}
      <div className="absolute inset-0 z-[1] opacity-[0.04]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}
      />

      {/* Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-5 sm:px-8 md:px-12 lg:px-16 pt-24 md:pt-0">
        <div className="flex flex-col items-center md:items-start text-center md:text-left max-w-3xl">
          {/* Badge */}
          <div
            ref={badgeRef}
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 mb-8 opacity-0"
          >
            <IconBookmark size={14} className="text-pumpkinToast-300" />
            <span className="text-xs font-medium text-white/90 uppercase tracking-widest">
              Islamic Research Center
            </span>
          </div>

          {/* Main Title */}
          <h1
            ref={titleRef}
            className="font-AndoraModernSerif font-bold text-white text-5xl sm:text-6xl md:text-7xl lg:text-8xl leading-[1.1] tracking-tight mb-4 opacity-0"
          >
            Darul Hadees
          </h1>

          {/* Arabic subtitle */}
          <h2
            ref={subtitleRef}
            className="font-cairo text-pumpkinToast-400 text-xl sm:text-2xl md:text-4xl lg:text-5xl font-bold leading-tight mb-8 opacity-0"
            style={{ direction: 'rtl' }}
          >
            دار الحديث الإسلامية
          </h2>

          {/* Description */}
          <p
            ref={descRef}
            className="text-white/80 text-base sm:text-lg md:text-xl leading-relaxed max-w-2xl font-light mb-10 opacity-0"
          >
            All of Islam's beliefs, customs, and practices are based on divine revelations. Explore the timeless teachings of Islam
            through authentic Hadith and scholarly research.
          </p>

          {/* CTA Buttons */}
          <div
            ref={ctaRef}
            className="flex flex-wrap gap-4 justify-center md:justify-start opacity-0"
          >
            <Link
              to="/courses"
              className="group inline-flex items-center gap-2 px-7 py-3.5 bg-pumpkinToast-500 hover:bg-pumpkinToast-400 text-white font-semibold rounded-xl shadow-lg shadow-pumpkinToast-500/30 transition-all duration-300 hover:shadow-xl hover:shadow-pumpkinToast-500/40 hover:-translate-y-0.5"
            >
              Explore Courses
              <IconArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
            </Link>
            <Link
              to="/about"
              className="inline-flex items-center gap-2 px-7 py-3.5 bg-white/10 hover:bg-white/20 text-white font-semibold rounded-xl border border-white/25 backdrop-blur-sm transition-all duration-300 hover:-translate-y-0.5"
            >
              Learn More
            </Link>
          </div>
        </div>
      </div>

      {/* Bottom gradient fade into next section */}
      <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-gray-50 via-[#eceaeaad] to-transparent z-10" />
    </section>
  );
}
